import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, Paperclip, Sparkles, Loader2 } from 'lucide-react';
import { crmNotesAPI, smsCopilotAPI } from '@/lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';

const NOTE_TYPES = ['call', 'email', 'meeting', 'internal', 'contracting'];

export function AddNoteDialog({ open, onOpenChange, subjectType, subjectId, onCreated }) {
  const [noteType, setNoteType] = useState('call');
  const [body, setBody] = useState('');
  const [visibleToAgent, setVisibleToAgent] = useState(false);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  // Phase 27.37.K — AI polish state.
  const [polishing, setPolishing] = useState(false);
  const [polishId, setPolishId] = useState(null);
  const [polishOriginal, setPolishOriginal] = useState('');
  const [polishSentiment, setPolishSentiment] = useState('');

  const reset = () => {
    setNoteType('call'); setBody(''); setVisibleToAgent(false); setFiles([]);
    setPolishId(null); setPolishOriginal(''); setPolishSentiment('');
  };

  const runPolish = async () => {
    if (!body.trim() || body.trim().length < 5) {
      toast.error('Write a few phrases first, then AI Polish will structure them.');
      return;
    }
    setPolishing(true);
    try {
      const { data } = await smsCopilotAPI.crmNotePolish({
        subject_type: subjectType,
        subject_id: subjectId,
        raw_notes: body,
        note_type: noteType,
      });
      setBody(data.polished_markdown || body);
      setPolishOriginal(data.polished_markdown || '');
      setPolishId(data.polish_id);
      setPolishSentiment(data.sentiment || '');
      toast.success('Note polished — review and adjust before saving.');
    } catch (e) {
      toast.error(getErrorMessage(e, 'AI polish failed — try again'));
    } finally { setPolishing(false); }
  };

  const submit = async () => {
    if (!body.trim()) {
      toast.error('Note body is required');
      return;
    }
    setSaving(true);
    try {
      const { data: note } = await crmNotesAPI.create({
        subject_type: subjectType,
        subject_id: subjectId,
        note_type: noteType,
        body: body.trim(),
        visible_to_agent: subjectType === 'agent' ? visibleToAgent : false,
      });
      // Attach files
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append('file', f);
          await crmNotesAPI.uploadAttachment(note.note_id, fd);
        } catch (e) {
          toast.error(`Failed to attach ${f.name}`);
        }
      }
      toast.success('Note added');
      // Phase 27.37.K — close AI polish audit loop if this note was
      // seeded from a polish call.
      if (polishId) {
        try {
          await smsCopilotAPI.crmNotePolishUsed({
            polish_id: polishId,
            edited: body.trim() !== polishOriginal.trim(),
            final_body: body,
          });
        } catch (_) { /* audit best-effort */ }
      }
      reset();
      onOpenChange(false);
      onCreated?.(note);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent data-testid="add-note-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>
            Notes are permanent. Only KR administrators may delete a note, and only with a reason.
            Markdown is supported: **bold**, *italic*, - lists, [links](https://...).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Note type</Label>
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger data-testid="add-note-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {NOTE_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Body</Label>
              <div className="flex items-center gap-2">
                {polishSentiment && (
                  <Badge variant="outline" className={`text-[10px] font-normal ${
                    polishSentiment === 'hot' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                    polishSentiment === 'warm' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    polishSentiment === 'cool' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                    polishSentiment === 'cold' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                    'bg-violet-50 text-violet-700 border-violet-200'
                  }`}>
                    Sentiment: {polishSentiment}
                  </Badge>
                )}
                {(subjectType === 'lead' || subjectType === 'prospect' || subjectType === 'agent') && (
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={runPolish}
                    disabled={polishing || !body.trim() || body.trim().length < 5}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50 h-7"
                    data-testid="crm-note-ai-polish-btn"
                  >
                    {polishing
                      ? <Loader2 size={11} className="mr-1 animate-spin" />
                      : <Sparkles size={11} className="mr-1" />}
                    AI Polish
                  </Button>
                )}
              </div>
            </div>
            <Textarea
              rows={8}
              placeholder="Jot down a quick summary of the call/meeting — AI Polish will structure it. Markdown is supported."
              value={body}
              onChange={(e) => { setBody(e.target.value); if (polishId) { setPolishId(null); setPolishSentiment(''); } }}
              data-testid="add-note-body"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Markdown supported. Line breaks preserved. Use <strong>AI Polish</strong> after jotting raw notes to structure into Summary / Key points / Next steps.
            </div>
          </div>

          {subjectType === 'agent' && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="visible-to-agent"
                checked={visibleToAgent}
                onCheckedChange={(v) => setVisibleToAgent(!!v)}
                data-testid="add-note-visibility"
              />
              <label htmlFor="visible-to-agent" className="text-sm">
                <div>Also visible to the agent</div>
                <div className="text-xs text-slate-500">
                  KR and the upline agency always see this note. Check this box to share it with the agent as well.
                </div>
              </label>
            </div>
          )}

          <div>
            <Label>Attachments (optional)</Label>
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid="add-note-file-input"
              className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3
                         file:rounded-md file:border-0 file:text-sm file:font-medium
                         file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, idx) => (
                  <li key={idx} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span className="flex items-center gap-1"><Paperclip size={12} /> {f.name} · {(f.size/1024).toFixed(1)} KB</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))}>
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="add-note-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="add-note-submit">
            {saving ? 'Saving…' : 'Add Note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddNoteDialog;
